import React, { type ComponentType, useCallback, useMemo, useState } from 'react';
import { type DrawerProps } from '@atlaskit/drawer';
import {
	type CompletionStatus,
	Journeys,
	type JourneyTargetProductDefinition,
} from '@atlassiansox/cross-flow-api-internals';
import { type TargetType } from '@atlassiansox/cross-flow-base-types';
import type CrossFlowIntegration from '@atlassiansox/cross-flow-react';
import { type ProductStoreRoute } from '@atlassiansox/product-store-react';
import type ProductStoreIntegration from '@atlassiansox/product-store-react';
import {
	getBenefitsPageRoute,
	getDiscoverProductsPageRoute,
} from '@atlassiansox/product-store-react';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { Header } from './header';
import { type IntegrationViewProps } from './types';
import { getDrawerStyleOverrides } from './styles';
import { toCrossFlowOrigin } from './utils/toCrossFlowOrigin';
import { source } from '../constants';
import { flushSync } from 'react-dom';

// todo: replace productStoreVisible and crossFlowVisible with a single prop
interface ProductStoreHomeViewState {
	productStoreVisible: true;
	crossFlowVisible: false;
	route: ProductStoreRoute;
}

interface ProductStoreProductViewState {
	productStoreVisible: true;
	crossFlowVisible: false;
	targetProduct: TargetType;
	route: ProductStoreRoute;
}

interface CrossFlowViewState {
	crossFlowVisible: true;
	productStoreVisible: false;
	targetProduct: TargetType;
	route: ProductStoreRoute;
}

export type ViewState =
	| ProductStoreHomeViewState
	| ProductStoreProductViewState
	| CrossFlowViewState;

interface Injectables {
	ProductStoreComponent: typeof ProductStoreIntegration;
	CrossFlowComponent: typeof CrossFlowIntegration;
	DrawerComponent: ComponentType<DrawerProps>;
}

const getInitialState = (data: JourneyTargetProductDefinition): ViewState => {
	switch (data.journey) {
		case Journeys.GET_STARTED: {
			return {
				productStoreVisible: false,
				crossFlowVisible: true,
				targetProduct: data.targetProduct,
				route: getBenefitsPageRoute(data.targetProduct),
			};
		}
		case Journeys.DECIDE: {
			return {
				productStoreVisible: true,
				crossFlowVisible: false,
				targetProduct: data.targetProduct,
				route: getBenefitsPageRoute(data.targetProduct),
			};
		}
		case Journeys.DISCOVER:
		default: {
			return {
				productStoreVisible: true,
				crossFlowVisible: false,
				route: getDiscoverProductsPageRoute(),
			};
		}
	}
};

export const IntegrationView: ComponentType<IntegrationViewProps & Injectables> = (props) => {
	const {
		journey,
		locale,
		sourceComponent,
		sourceContext,
		zIndex,
		DrawerComponent,
		ProductStoreComponent,
		CrossFlowComponent,
		theme,
		experimentalOptions,
		extensions,
		onClose,
		onAnalyticsEvent,
		originProduct,
		onHandShake,
		plugins,
		edgePrefix,
		redirectToWac,
		env,
		cloudId,
		isInTblz852ExperimentCohort,
	} = props;

	const isStandaloneProductPage = props.journey === Journeys.DECIDE;

	const [isDrawerOpen, setIsDrawerOpen] = useState(true);
	const [completionStatus, setCompletionStatus] = useState<CompletionStatus>({});
	const [viewState, setViewState] = useState<ViewState>(getInitialState(props));

	const analyticsContext = useMemo(
		() => ({
			cloudId,
			originProduct,
			targetProduct: (viewState as unknown as { targetProduct?: string })?.targetProduct,
			journey,
			source,
		}),
		[cloudId, originProduct, journey, viewState],
	);

	const onCloseDrawer = useCallback(
		(e: React.SyntheticEvent<HTMLElement, Event>, event?: UIAnalyticsEvent | null) => {
			if (event) {
				// Emit the drawer dismissed event from the atlaskit channel
				// using the product provided analytics client
				const { context, payload } = event;
				onAnalyticsEvent({
					context: [context, analyticsContext],
					payload: { ...payload, actionSubjectId: 'crossFlowDrawer' },
				});
			}
			setIsDrawerOpen(false);
		},
		[onAnalyticsEvent, analyticsContext],
	);

	const closeDrawer = useCallback(() => {
		setIsDrawerOpen(false);
	}, []);

	const productStoreOnTryClicked = useCallback((product: TargetType) => {
		setViewState((prevState) => ({
			...prevState,
			crossFlowVisible: true,
			productStoreVisible: false,
			targetProduct: product,
		}));
	}, []);

	const productStoreOnChangeRoute = useCallback((route: ProductStoreRoute) => {
		setViewState((prevState) => ({
			...prevState,
			route,
		}));
	}, []);

	const crossFlowOnClose = useCallback(() => {
		if (journey === Journeys.GET_STARTED) {
			/**
			 * User has completed Get Started journey.
			 * Cross Flow Frontend does not provide status, but only invokes the callback
			 * when activation was complete
			 *
			 * NOTE: flushSync required to ensure completion status is updated before
			 * running the drawer status.
			 */
			flushSync(() => {
				setCompletionStatus({
					success: true,
				});
			});
		}

		closeDrawer();
	}, [closeDrawer, journey]);

	const drawerOnCloseComplete = useCallback(() => {
		onClose(completionStatus);
	}, [onClose, completionStatus]);

	const onProductStoreHandshake = useCallback(() => {
		onHandShake('productStoreSpa');
	}, [onHandShake]);

	const onCrossFlowHandshake = useCallback(() => {
		onHandShake('crossFlowSpa');
	}, [onHandShake]);

	// go/trailblazerexperiments
	// TBLZ-852: Product Store - Personal Use Case
	const onBackToAllProductsClick = useCallback(
		(_: any, analyticEvent: UIAnalyticsEvent) => {
			const { context, payload } = analyticEvent;
			onAnalyticsEvent({
				context: [context, analyticsContext],
				payload: {
					...payload,
					actionSubjectId: 'backToAllProductsButton',
				},
			});
			setViewState(() => ({
				crossFlowVisible: false,
				productStoreVisible: true,
				route: getDiscoverProductsPageRoute(),
			}));
		},
		[onAnalyticsEvent, analyticsContext],
	);

	const onCloseDrawerFn = useCallback(
		(_: any, analyticEvent: UIAnalyticsEvent) => {
			const { context, payload } = analyticEvent;
			onAnalyticsEvent({
				context: [context, analyticsContext],
				payload: {
					...payload,
					actionSubjectId: 'closeDrawerButton',
				},
			});
			drawerOnCloseComplete();
			closeDrawer();
		},
		[drawerOnCloseComplete, closeDrawer, onAnalyticsEvent, analyticsContext],
	);

	const shouldShowExperimentHeader =
		isInTblz852ExperimentCohort &&
		sourceContext === 'more' &&
		analyticsContext.targetProduct !== 'loom';
	// TBLZ-852

	return (
		<DrawerComponent
			isOpen={isDrawerOpen}
			width="full"
			onClose={onCloseDrawer}
			onCloseComplete={drawerOnCloseComplete}
			overrides={getDrawerStyleOverrides(shouldShowExperimentHeader)}
			zIndex={zIndex}
		>
			{shouldShowExperimentHeader ? (
				<Header
					viewState={viewState}
					handleClose={onCloseDrawerFn}
					handleBackToAllProducts={onBackToAllProductsClick}
				/>
			) : null}
			{viewState.productStoreVisible && (
				<ProductStoreComponent
					edgePrefix={edgePrefix}
					cloudId={cloudId}
					locale={locale}
					route={viewState.route}
					originProduct={originProduct}
					theme={theme}
					experimentalOptions={experimentalOptions}
					extensions={extensions}
					onClose={closeDrawer}
					onTryClicked={productStoreOnTryClicked}
					onAnalyticsEvent={onAnalyticsEvent}
					onChangeRoute={productStoreOnChangeRoute}
					plugins={plugins}
					onHandShake={onProductStoreHandshake}
					isStandaloneProductPage={isStandaloneProductPage}
					isLinkExpansion={redirectToWac}
					env={env}
					sourceContext={sourceContext}
					sourceComponent={sourceComponent}
					journey={journey}
				/>
			)}
			{viewState.crossFlowVisible && (
				<CrossFlowComponent
					edgePrefix={edgePrefix}
					onClose={crossFlowOnClose}
					cloudId={cloudId}
					locale={locale}
					sourceContext={sourceContext}
					sourceComponent={sourceComponent}
					targetProduct={viewState.targetProduct}
					originProduct={toCrossFlowOrigin(originProduct)}
					theme={theme}
					experimentalOptions={experimentalOptions}
					extensions={extensions}
					onAnalyticsEvent={onAnalyticsEvent}
					onHandShake={onCrossFlowHandshake}
					plugins={plugins}
					env={env}
					journey={journey}
				/>
			)}
		</DrawerComponent>
	);
};
